import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { saveImageFromUrl } from '../utils/saveImage';
import { lockBodyScroll } from '../utils/scrollLock';
import {
  X, Loader, ImagePlus, Puzzle, CheckCircle2, AlertTriangle, ShieldAlert, Lock, Download, ChevronDown, ChevronUp,
} from 'lucide-react';

// 证券上传弹层：主图 + 客户对话图 + 客户资料图 → 识别 → 核对/修改字段 → 确认无误 → 服务端合成一张最终图
//   主图：首次上传后，之后每次打开自动带出（想换直接点方框替换）；对话图、资料图每次重新上传
//   targetUser：代传时传 { id, name }（管理员/代理商代对方上传）；不传=本人
//   onChanged：记录发生变化（确认提交等）时回调，用于刷新号码列表
//   onFinish：结果页的“完成本次”按钮回调（不传则不显示该按钮）
const FIELD_LABEL = { wechat_id: '微信号', nickname: '昵称', phone: '电话', operator_wx: '操作微信号' };

function SlotBox({ label, hint, previewUrl, badge, onPick }) {
  const inputRef = useRef(null);
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label}
        {badge && <span style={{ fontSize: 10, color: 'var(--c-success)', fontWeight: 600 }}>{badge}</span>}
      </div>
      <div
        onClick={() => inputRef.current?.click()}
        style={{
          aspectRatio: '3 / 4', borderRadius: 12, cursor: 'pointer', overflow: 'hidden',
          border: previewUrl ? '1.5px solid var(--c-border)' : '1.5px dashed var(--c-text-tertiary)',
          background: previewUrl ? '#000' : 'var(--c-bg)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
        }}
      >
        {previewUrl
          ? <img src={previewUrl} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <>
              <ImagePlus size={28} color="var(--c-text-tertiary)" />
              <span style={{ fontSize: 11, color: 'var(--c-text-tertiary)' }}>点击选择</span>
            </>}
      </div>
      <div style={{ fontSize: 10, color: 'var(--c-text-tertiary)', marginTop: 4, textAlign: 'center' }}>{hint}</div>
      <input
        ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onPick(f); }}
      />
    </div>
  );
}

export default function PuzzleUploadModal({ targetUser, onClose, onChanged, onFinish }) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const targetId = targetUser?.id || null;

  const [step, setStep] = useState('pick');           // pick | busy | edit | invalid | done
  const [files, setFiles] = useState({ main: null, chat: null, profile: null });
  const [previews, setPreviews] = useState({ main: null, chat: null, profile: null });
  const [mainRemote, setMainRemote] = useState(null); // { url, operator_wx }
  const [notice, setNotice] = useState(null);         // { kind, message, phone? } 无效/无包 时展示
  const [record, setRecord] = useState(null);
  const [fields, setFields] = useState({ phone: '', wechat_id: '', nickname: '', operator_wx: '' });
  const [ocrStatus, setOcrStatus] = useState('SUCCESS'); // SUCCESS | OCR_FAILED
  const [ocrMessage, setOcrMessage] = useState('');
  const [ocrText, setOcrText] = useState('');
  const [showOcrText, setShowOcrText] = useState(false);
  const [nextSeq, setNextSeq] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [fieldErr, setFieldErr] = useState('');
  const [result, setResult] = useState(null);         // 确认后的结果 { status, compose_ok, record }
  const [saving, setSaving] = useState(false);
  const urlsRef = useRef([]);

  // 弹窗打开期间锁定底层页面滚动
  useEffect(() => lockBodyScroll(), []);

  // 卸载时释放本地预览 URL
  useEffect(() => () => { urlsRef.current.forEach(u => URL.revokeObjectURL(u)); }, []);

  // 自动带出上次用的主图
  useEffect(() => {
    let alive = true;
    api.get('/puzzle/main-image', { params: targetId ? { userId: targetId } : {} })
      .then(r => { if (alive && r.data?.url) setMainRemote({ url: r.data.url, operator_wx: r.data.operator_wx || '' }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [targetId]);

  const pickFile = (slot, file) => {
    if (!file.type.startsWith('image/')) { showToast('请选择图片文件', 'error'); return; }
    if (file.size > 15 * 1024 * 1024) { showToast('图片不能超过 15MB', 'error'); return; }
    const url = URL.createObjectURL(file);
    urlsRef.current.push(url);
    setFiles(f => ({ ...f, [slot]: file }));
    setPreviews(p => ({ ...p, [slot]: url }));
  };

  const hasMain = !!(files.main || mainRemote);
  const canStart = !!(files.chat && files.profile && hasMain);

  const applyRecord = useCallback((rec) => {
    setRecord(rec);
    setFields({
      phone: rec.phone || '', wechat_id: rec.wechat_id || '', nickname: rec.nickname || '', operator_wx: rec.operator_wx || '',
    });
  }, []);

  const startOcr = async () => {
    if (!canStart) return;
    setStep('busy');
    setNotice(null);
    setFieldErr('');
    try {
      const fd = new FormData();
      if (files.main) fd.append('main', files.main);
      fd.append('chat', files.chat);
      fd.append('profile', files.profile);
      if (targetId) fd.append('targetUserId', String(targetId));
      const res = await api.post('/puzzle/ocr', fd, { timeout: 120000 });
      const d = res.data || {};
      // 换了主图：服务端已保存并会自动沿用。这里清掉本地待传的主图，避免“重新上传/下一条”时重复上传并重复识别主图
      if (files.main) {
        setFiles(f => ({ ...f, main: null }));
        setPreviews(p => ({ ...p, main: null }));
        api.get('/puzzle/main-image', { params: targetId ? { userId: targetId } : {} })
          .then(r => { if (r.data?.url) setMainRemote({ url: r.data.url, operator_wx: r.data.operator_wx || '' }); }).catch(() => {});
      }
      if (d.status === 'NO_ACTIVE_PACKAGE') {
        setNotice({ kind: 'nopkg', message: d.message || '请先到接量页领取数据包' });
        setStep('invalid');
        return;
      }
      if (d.status === 'INVALID') {
        setNotice({ kind: 'invalid', message: d.message || '该号码无效', phone: d.phone });
        setStep('invalid');
        return;
      }
      if (d.record) {
        applyRecord(d.record);
        setOcrStatus(d.status === 'OCR_FAILED' ? 'OCR_FAILED' : 'SUCCESS');
        setOcrMessage(d.message || '');
        setOcrText(d.ocr_text || '');
        setNextSeq(d.next_seq ?? null);
        setStep('edit');
        return;
      }
      setNotice({ kind: 'invalid', message: '识别没有返回结果，请重试' });
      setStep('invalid');
    } catch (err) {
      showToast(err.response?.data?.error || (err.code === 'ECONNABORTED' ? '识别超时，请重试' : '识别失败，请重试'), 'error');
      setStep('pick');
    }
  };

  // 是否受“改电话要校验”约束：本人（非管理员）操作自己的记录；管理员/代为操作的上级不校验
  const restricted = !!record && user?.id === record.user_id && user?.role !== 'admin';
  const locked = !!record?.edit_locked;

  // 把改动过的字段逐个保存到服务端（电话放最前，因为可能触发锁定）。返回是否全部成功。
  const flushDirty = async () => {
    if (!record) return false;
    const order = ['phone', 'wechat_id', 'nickname', 'operator_wx'];
    let cur = record;
    for (const f of order) {
      const val = String(fields[f] ?? '').trim();
      if (val === String(cur[f] ?? '')) continue;
      if (f === 'phone' && !val) { setFieldErr('请填写电话号码'); return false; }
      try {
        const res = await api.post('/puzzle/ocr-edit', { recordId: record.id, field: f, value: val });
        const d = res.data || {};
        if (d.status === 'OK') { cur = d.record; applyRecord(d.record); continue; }
        if (d.status === 'LOCKED') { setRecord(r => ({ ...r, edit_locked: true })); }
        setFieldErr(d.message || `${FIELD_LABEL[f]}保存失败`);
        return false;
      } catch (err) {
        setFieldErr(err.response?.data?.error || `${FIELD_LABEL[f]}保存失败`);
        return false;
      }
    }
    return true;
  };

  const handleConfirm = async () => {
    if (!record || confirming) return;
    setConfirming(true);
    setFieldErr('');
    try {
      const ok = await flushDirty();
      if (!ok) return;
      const res = await api.post('/puzzle/record/confirm', { recordId: record.id }, { timeout: 60000 });
      const d = res.data || {};
      if (d.status === 'CONFIRMED' || d.status === 'PENDING_REVIEW') {
        setResult(d);
        setStep('done');
        onChanged && onChanged();
        return;
      }
      if (d.status === 'ALREADY_CONFIRMED') { setFieldErr('这条记录已经提交过了'); return; }
      if (d.status === 'LOCKED') setRecord(r => ({ ...r, edit_locked: true }));
      setFieldErr(d.message || '提交失败，请重试');
    } catch (err) {
      setFieldErr(err.response?.data?.error || '提交失败，请重试');
    } finally {
      setConfirming(false);
    }
  };

  const handleSaveImage = async () => {
    const url = result?.record?.images?.combined;
    if (!url) return;
    setSaving(true);
    try {
      const r = await saveImageFromUrl(url, `证券-${result.record.package_name}-${result.record.seq_no ?? '待核实'}.jpg`);
      if (r === 'shared') showToast('已打开保存面板', 'success');
      else if (r === 'download') showToast('已下载图片', 'success');
    } catch {
      showToast('保存失败，请长按图片保存', 'error');
    } finally { setSaving(false); }
  };

  const busy = step === 'busy';
  const close = () => { if (!busy && !confirming) onClose && onClose(); };

  const card = { background: 'var(--c-bg)', borderRadius: 12, padding: 12 };
  const inputStyle = { fontSize: 15, padding: '10px 12px' };

  const body = (() => {
    if (step === 'pick' || step === 'busy') {
      return (
        <>
          <div style={{ display: 'flex', gap: 10 }}>
            <SlotBox
              label="主图" hint={mainRemote && !files.main ? '已带出上次的，点击更换' : '首次上传，之后自动沿用'}
              previewUrl={previews.main || mainRemote?.url} badge={mainRemote && !files.main ? '已带出' : ''}
              onPick={f => pickFile('main', f)}
            />
            <SlotBox label="客户对话图" hint="每次重新上传" previewUrl={previews.chat} onPick={f => pickFile('chat', f)} />
            <SlotBox label="客户资料图" hint="每次重新上传" previewUrl={previews.profile} onPick={f => pickFile('profile', f)} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', lineHeight: 1.6, margin: '14px 0' }}>
            识别后只会在<strong>您当前领取的数据包</strong>里匹配号码。主图用来识别“操作微信号”，客户的号码来自对话图和资料图。
          </div>
          <button className="btn btn-primary" onClick={startOcr} disabled={!canStart || busy}
            style={{ height: 48, fontSize: 16, borderRadius: 12 }}>
            {busy ? <><Loader size={17} className="spin" /> 识别中，请稍候…</> : '开始识别'}
          </button>
        </>
      );
    }

    if (step === 'invalid') {
      const nopkg = notice?.kind === 'nopkg';
      return (
        <>
          <div style={{ ...card, background: 'rgba(255,59,48,0.06)', border: '1px solid rgba(255,59,48,0.2)', textAlign: 'center', padding: 20 }}>
            <AlertTriangle size={36} color="var(--c-danger)" />
            <div style={{ fontSize: 16, fontWeight: 700, marginTop: 8 }}>{nopkg ? '还没有领取数据包' : '无效号码'}</div>
            {notice?.phone && <div style={{ fontSize: 15, fontWeight: 600, marginTop: 6, letterSpacing: 0.5 }}>{notice.phone}</div>}
            <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginTop: 6, lineHeight: 1.6 }}>{notice?.message}</div>
            {!nopkg && <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 6 }}>不会生成记录，也不计入结算</div>}
          </div>
          <button className="btn btn-primary" style={{ marginTop: 14, height: 46 }}
            onClick={() => { setFiles(f => ({ ...f, chat: null, profile: null })); setPreviews(p => ({ ...p, chat: null, profile: null })); setStep('pick'); }}>
            重新上传
          </button>
        </>
      );
    }

    if (step === 'edit' && record) {
      const failed = ocrStatus === 'OCR_FAILED';
      const willReview = failed && restricted;   // 本人手填 → 待核实
      return (
        <>
          {failed ? (
            <div style={{ ...card, background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.25)', display: 'flex', gap: 8 }}>
              <ShieldAlert size={18} color="var(--c-accent)" style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 13, lineHeight: 1.6, color: '#9a6400' }}>
                <strong>识别失败</strong>：{ocrMessage || '没有从图片里读出号码'}
                {restricted ? '。请手动填写号码后提交，提交后会通知管理员核实，核实通过后才计入单数。' : '。请手动填写号码（代为操作，不需要核实）。'}
              </div>
            </div>
          ) : (
            <div style={{ ...card, background: 'rgba(52,199,89,0.08)', border: '1px solid rgba(52,199,89,0.25)', display: 'flex', gap: 8 }}>
              <CheckCircle2 size={18} color="var(--c-success)" style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 13, lineHeight: 1.6, color: '#1b7d35' }}><strong>匹配成功</strong>：{ocrMessage}</div>
            </div>
          )}

          {locked && (
            <div style={{ ...card, marginTop: 10, background: 'rgba(255,59,48,0.06)', border: '1px solid rgba(255,59,48,0.2)', display: 'flex', gap: 8 }}>
              <Lock size={17} color="var(--c-danger)" style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 13, lineHeight: 1.6, color: '#c5221f' }}>
                这条记录已被锁定（填写的号码不在图片识别出的文字里），不能再修改和提交，请联系管理员或代理商处理。
              </div>
            </div>
          )}

          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 4 }}>数据名 · 自动识别</div>
              <div style={{ ...card, fontSize: 15, fontWeight: 600 }}>{record.package_name}</div>
            </div>
            {['wechat_id', 'nickname'].map(f => (
              <div key={f}>
                <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 4 }}>{FIELD_LABEL[f]}</div>
                <input className="input" style={inputStyle} value={fields[f]} disabled={locked}
                  onChange={e => setFields(s => ({ ...s, [f]: e.target.value }))} placeholder={`请输入${FIELD_LABEL[f]}`} />
              </div>
            ))}
            <div>
              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                电话 · 可修改
                {!failed && !locked && (
                  <span style={{ fontSize: 10, color: 'var(--c-success)', fontWeight: 600 }}>
                    {String(fields.phone) === String(record.phone || '') ? '识别一致' : '已修改，保存时校验'}
                  </span>
                )}
              </div>
              <input className="input" style={inputStyle} inputMode="numeric" maxLength={13} value={fields.phone} disabled={locked}
                onChange={e => setFields(s => ({ ...s, phone: e.target.value }))} placeholder="请输入 11 位手机号" />
              <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 4, lineHeight: 1.5 }}>
                {failed
                  ? (restricted ? '图上没有号码可对比，请仔细核对；号码须在您领取的数据包内。' : '号码须在该用户领取的数据包内。')
                  : (restricted ? '改电话时，新号码必须出现在图片识别出的文字里，对不上这条记录会被锁定。' : '代为操作，改电话不校验图片文字（号码仍须在数据包内）。')}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 4 }}>操作微信号</div>
              <input className="input" style={inputStyle} value={fields.operator_wx} disabled={locked}
                onChange={e => setFields(s => ({ ...s, operator_wx: e.target.value }))} placeholder="从主图识别，可修改" />
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 4 }}>成功单数 · 提交后按顺序计入</div>
              <div style={{ ...card, fontSize: 15, fontWeight: 600, color: willReview ? 'var(--c-accent)' : 'var(--c-text)' }}>
                {willReview ? '待核实，通过后才计单数' : (nextSeq != null ? `第 ${nextSeq} 单` : '—')}
              </div>
            </div>
          </div>

          {ocrText && (
            <div style={{ marginTop: 12 }}>
              <button onClick={() => setShowOcrText(v => !v)} style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--c-text-secondary)', padding: '4px 0',
              }}>
                {showOcrText ? <ChevronUp size={14} /> : <ChevronDown size={14} />} 图片识别出的文字（校验依据）
              </button>
              {showOcrText && (
                <div style={{ ...card, fontSize: 12, lineHeight: 1.7, color: 'var(--c-text-secondary)', whiteSpace: 'pre-wrap', maxHeight: 140, overflowY: 'auto', wordBreak: 'break-all' }}>
                  {ocrText}
                </div>
              )}
            </div>
          )}

          {fieldErr && (
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--c-danger)', lineHeight: 1.5 }}>{fieldErr}</div>
          )}

          <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 12, lineHeight: 1.6 }}>
            点击后本条记录即提交，<strong>提交后您不能再修改</strong>，请再核对一遍。
          </div>
          <button className="btn btn-success" onClick={handleConfirm} disabled={confirming || locked}
            style={{ marginTop: 10, height: 48, fontSize: 16, borderRadius: 12 }}>
            {confirming ? <><Loader size={17} className="spin" /> 提交中…</> : '确认无误，生成图片'}
          </button>
        </>
      );
    }

    if (step === 'done' && result) {
      const rec = result.record;
      const pending = result.status === 'PENDING_REVIEW';
      return (
        <>
          <div style={{ textAlign: 'center', padding: '6px 0 12px' }}>
            {pending ? <ShieldAlert size={36} color="var(--c-accent)" /> : <CheckCircle2 size={36} color="var(--c-success)" />}
            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 6 }}>{pending ? '已提交，待核实' : '生成成功！'}</div>
            <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginTop: 4, lineHeight: 1.6 }}>
              {pending ? '号码为手动填写，已通知管理员核实。核实通过后计入成功单数。' : `本次成功单数 第 ${rec.seq_no} 单 · ${rec.package_name}`}
            </div>
          </div>

          {rec.images?.combined ? (
            <img src={rec.images.combined} alt="最终图片"
              style={{ width: '100%', borderRadius: 12, border: '1px solid var(--c-border)', background: '#fff', display: 'block' }} />
          ) : (
            <div style={{ ...card, background: 'rgba(255,149,0,0.08)', fontSize: 13, color: '#9a6400', lineHeight: 1.6 }}>
              最终图片暂时没有生成（{result.compose_reason === 'unavailable' ? '服务器缺少图片处理组件' : result.compose_reason === 'fonts_missing' ? '服务器字体还在准备中' : '合成出错'}），记录已提交，请联系管理员在后台点“重新合成”。
            </div>
          )}

          {rec.images?.combined && (
            <button className="btn btn-primary" onClick={handleSaveImage} disabled={saving}
              style={{ marginTop: 12, height: 46 }}>
              <Download size={16} /> {saving ? '保存中…' : '保存图片'}
            </button>
          )}
          <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', textAlign: 'center', margin: '10px 0', lineHeight: 1.6 }}>
            已确认提交，不能再修改。如有错误请联系管理员或代理商处理。
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-outline" style={{ height: 46 }} onClick={() => onClose && onClose()}>继续下一条</button>
            {onFinish && (
              <button className="btn btn-ghost" style={{ height: 46, border: '1px solid var(--c-border)' }}
                onClick={() => { onClose && onClose(); onFinish(); }}>完成本次</button>
            )}
          </div>
        </>
      );
    }
    return null;
  })();

  return createPortal(
    <div
      onClick={close}
      style={{ position: 'fixed', inset: 0, zIndex: 9997, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog" aria-modal="true"
        style={{
          background: 'var(--c-surface)', borderRadius: '22px 22px 0 0', width: '100%', maxWidth: 480,
          maxHeight: '93vh', display: 'flex', flexDirection: 'column',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 18px 10px' }}>
          <Puzzle size={18} color="var(--c-primary)" />
          <span style={{ fontSize: 17, fontWeight: 700, flex: 1 }}>
            {step === 'edit' ? '核对识别结果' : step === 'done' ? '提交结果' : '上传三张图片'}
            {targetUser?.name ? <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--c-text-secondary)', marginLeft: 8 }}>为 {targetUser.name} 代传</span> : null}
          </span>
          <button onClick={close} disabled={busy || confirming}
            style={{ background: 'var(--c-bg)', border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} color="var(--c-text-secondary)" />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '4px 18px 20px' }}>{body}</div>
      </div>
    </div>,
    document.body
  );
}
